import { useI18n } from 'next-localization';
import { useEffect, useMemo, useRef, useState } from 'react';

import { Filter, FilterOption } from 'hooks/useFilters';
import useWindowDimensions from 'hooks/useWindowDimensions';
import RadioButtonFieldset from 'components/Generic/Forms/FormFields/RadioButtonFieldset';
import DatepickerFilter from 'components/Functional/Filters/DatePickerFilter';
import styles from '../../styles/MobileBaseFilters.module.scss';
import AsideModal from 'components/Generic/AsideModal';
import MobileBaseFiltersDropdown from 'components/Functional/Filters/MobileFilter/MobileBaseFiltersDropdown';
import MobileBaseFiltersActions from 'components/Functional/Filters/MobileFilter/MobileBaseFiltersActions';
import { useSelector } from 'react-redux';
import { getFundCentreData } from '@/store/fundCentre/selectors';

type Props = {
  className: string;
  filters: Filter[];
  needAggregateCount: (filter) => void;
  optionSelect: () => void;
  resetUnAppliedOptions: (option) => void;
  filterApply: () => void;
  clearFilters: () => void;
  isFetching: boolean;
  showDatePicker: boolean;
  selectedFilterOptions: Array<{
    id: string;
    selectedOptions: FilterOption[];
  }>;
  allOptions: FilterOption[];
  buttonFullWidth?: boolean;
};

const MobileBaseFilters = ({
  className,
  filters,
  needAggregateCount,
  optionSelect,
  resetUnAppliedOptions,
  filterApply,
  clearFilters,
  isFetching,
  showDatePicker,
  selectedFilterOptions,
  allOptions,
  buttonFullWidth,
}: Props) => {
  const { t } = useI18n();
  const mobileFiltersSection = useRef();
  const deviceType: string = useWindowDimensions();
  const { selectedDate } = useSelector(getFundCentreData);

  const datePickerFilters = {
    options: [],
    id: 'datePicker',
    displayNameKey: 'fundsFundCentreFiltersPricesDate',
  };

  const radioButtonsData = {
    defaultValue: 'datePickerView',

    optionList: {
      id: 'dateType',

      elements: [
        {
          type: 'Option',
          displayName: t('fundCentreFiltersChooseDate'),
          value: 'datePickerView',
        },
        {
          type: 'Option',
          displayName: t('fundCentreFiltersEnterDate'),
          value: 'inputView',
        },
      ],
    },
  };

  const [isActive, setIsActive] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState('');
  const [datePickerType, setDatePickerType] = useState('datePickerView');

  const toggleFiltersAside = () => {
    setIsActive((prev) => !prev);
  };

  const onToggleDropdown = (filterId) => {
    if (activeDropdown === filterId) {
      setActiveDropdown('');
    } else {
      setActiveDropdown(filterId);
      if (filterId !== 'datePicker') {
        needAggregateCount(filterId);
      }
    }
  };

  const clearFiltersHandled = () => {
    clearFilters();
    toggleFiltersAside();
  };

  const selectedOptionsLength = useMemo(() => {
    const result = selectedFilterOptions?.reduce((count, item) => {
      return count + item.selectedOptions.length;
    }, 0);

    return (selectedDate ? result + 1 : result) || 0;
  }, [selectedDate, selectedFilterOptions]);

  useEffect(() => {
    if (isActive) {
      needAggregateCount(filters[0]?.id);
      setActiveDropdown(filters[0]?.id || 'datePicker');
      mobileFiltersSection.current?.focus();
    } else {
      setActiveDropdown('');
      resetUnAppliedOptions(filters[0]);
    }
  }, [isActive]);

  return (
    <div className={className}>
      <button
        ref={mobileFiltersSection}
        className={`d-flex align-items-center filter-selector btn-naked ${
          buttonFullWidth ? 'w-100' : ''
        }`}
        onClick={toggleFiltersAside}
      >
        <p className="field-text">{`Filters (${selectedOptionsLength})`}</p>
        <em className="d-flex bi-sliders ms-auto" aria-hidden="true" />
      </button>
      <AsideModal positionX="left" active={isActive} onClose={() => setIsActive(false)}>
        <div
          className={`d-flex align-items-center ${styles['filters-summury']} ${styles['filter-dropdown-sum']}`}
        >
          <p className="me-3 h4">{t('filters')}</p>
          <span className={styles.separator} />
          <p className="ms-3 body-default">
            {selectedOptionsLength} of {allOptions.length}
          </p>
          <button
            className="ms-auto btn-naked"
            aria-label={t('close')}
            onClick={() => toggleFiltersAside()}
          >
            <em
              className={`d-flex ms-2 bi-x-lg ${styles['close-filters-icon']}`}
              aria-hidden="true"
            />
          </button>
        </div>
        {(deviceType === 'tablet' || deviceType === 'mobile') && showDatePicker && (
          <MobileBaseFiltersDropdown
            isOpen={activeDropdown === 'datePicker'}
            filter={datePickerFilters}
            toggleDropdown={onToggleDropdown}
            optionSelect={optionSelect}
            isFetching={isFetching}
          >
            <div
              className={`px-2 pb-2 ${
                activeDropdown !== 'datePicker' ? 'datepicker__inactive' : ''
              }`}
            >
              <span className="form-label">{t('selectDate')}</span>
              <RadioButtonFieldset
                fields={radioButtonsData}
                className="mb-2"
                changeButtonsGrid
                onUpdateValue={(value) => setDatePickerType(value)}
              />
              <DatepickerFilter
                inline={datePickerType === 'datePickerView'}
                showPopper={false}
                toggleFiltersAside={toggleFiltersAside}
              />
            </div>
          </MobileBaseFiltersDropdown>
        )}
        {filters.map((filter) => (
          <MobileBaseFiltersDropdown
            key={filter.id}
            filter={filter}
            isFetching={isFetching}
            isOpen={activeDropdown === filter.id}
            toggleDropdown={onToggleDropdown}
            optionSelect={optionSelect}
          />
        ))}
        <MobileBaseFiltersActions
          isFetching={isFetching}
          className={styles['filter-dropdown-nav']}
          clearFilters={clearFiltersHandled}
          filterApply={() => {
            filterApply();
            toggleFiltersAside();
          }}
        />
      </AsideModal>
    </div>
  );
};

export default MobileBaseFilters;
